import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { getNotifications, markAllNotificationsRead, getFrontendPipelineCounters } from '../lib/api';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ChatbotDrawer from './ChatbotDrawer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Building2,
  LayoutDashboard,
  Users,
  Briefcase,
  FileText,
  ShoppingCart,
  Settings,
  Bell,
  LogOut,
  Menu,
  X,
  ChevronDown,
  FolderKanban,
  CircleDollarSign,
  Wrench,
  MessageSquare,
  Coins,
  DollarSign,
  TrendingUp,
  Rocket,
  Bug,
} from 'lucide-react';

const roleLabels = {
  admin: 'Admin',
  project_manager: 'Project Manager',
  venditore: 'Operativo',
  cliente: 'Cliente',
  account: 'Account',
  hr: 'HR',
};

const roleColors = {
  admin: 'bg-red-100 text-red-700',
  project_manager: 'bg-orange-100 text-orange-700',
  venditore: 'bg-green-100 text-green-700',
  cliente: 'bg-purple-100 text-purple-700',
  account: 'bg-amber-100 text-amber-700',
  hr: 'bg-pink-100 text-pink-700',
};

export default function Layout({ children }) {
  const { user, logout, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [fePipelineToAssign, setFePipelineToAssign] = useState(0);

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!hasRole(['admin', 'hr', 'project_manager', 'supervisor'])) return;
    const load = async () => {
      try {
        const res = await getFrontendPipelineCounters();
        setFePipelineToAssign(res.data?.da_assegnare || 0);
      } catch (_err) { /* silent */ }
    };
    load();
    const interval = setInterval(load, 120000);
    return () => clearInterval(interval);
  }, [hasRole]);

  const loadNotifications = async () => {
    try {
      const res = await getNotifications();
      setNotifications(res.data.slice(0, 10));
      setUnreadCount(res.data.filter((n) => !n.is_read).length);
    } catch (err) {
      console.error('Error loading notifications:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(notifications.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Error marking notifications read:', err);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const navItems = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'venditore', 'cliente', 'account'] },
    { to: '/clients', icon: Building2, label: 'Clienti', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'account'] },
    { to: '/projects', icon: FolderKanban, label: 'Progetti', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'venditore', 'cliente', 'account'] },
    { to: '/project-management', icon: FolderKanban, label: 'Gestione Progetti', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'account', 'venditore'] },
    { to: '/frontend-pipeline', icon: Rocket, label: 'Pipeline Front-End', roles: ['admin', 'hr', 'account', 'project_manager', 'supervisor'], badge: fePipelineToAssign },
    { to: '/contracts', icon: FileText, label: 'Contratti', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'account'] },
    { to: '/upsells', icon: TrendingUp, label: 'Up-Sell', roles: ['admin', 'hr', 'project_manager', 'supervisor'] },
    { to: '/sales', icon: ShoppingCart, label: 'Vendite', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'venditore', 'cliente', 'account'] },
    { to: '/commissions', icon: CircleDollarSign, label: 'Success Fee', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'venditore', 'cliente', 'account'] },
    { to: '/compensations', icon: Coins, label: 'Compensi', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'venditore'] },
    { to: '/customer-feedback', icon: MessageSquare, label: 'Customer Feedback', roles: ['admin', 'hr', 'account', 'project_manager', 'supervisor'] },
    { to: '/toolkit', icon: Wrench, label: 'Toolkit', roles: ['admin', 'hr', 'project_manager', 'supervisor', 'account'] },
    { to: '/fiscal-credits', icon: Coins, label: 'Crediti Fiscali', roles: ['admin'] },
    { to: '/sales-roster', icon: Users, label: 'Rosa Venditori', roles: ['admin', 'hr'] },
    { to: '/users', icon: Users, label: 'Utenti', roles: ['admin', 'hr'] },
    { to: '/settings', icon: Settings, label: 'Impostazioni', roles: ['admin', 'hr'] },
    { to: '/admin/debug-pm', icon: Bug, label: 'Debug PM', roles: ['admin'] },
  ];

  const filteredNavItems = navItems.filter((item) => hasRole(item.roles));

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r transform transition-transform duration-200 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-vad.jpg" alt="VAD Brain" className="h-8 w-auto" />
            <span className="font-semibold text-gray-900">VAD Brain</span>
          </Link>
          <button className="lg:hidden p-2" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {filteredNavItems.map((item) => {
            const isActive = item.to === '/' ? location.pathname === '/' : location.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                  isActive
                    ? 'bg-orange-50 text-orange-700'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="flex-1">{item.label}</span>
                {item.badge > 0 && (
                  <Badge variant="secondary" className="bg-red-100 text-red-700 border-0 h-5 min-w-5 px-1.5 text-[10px] font-semibold">
                    {item.badge}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b flex items-center justify-between px-4">
          <button
            className="lg:hidden p-2 -ml-2"
            onClick={() => setSidebarOpen(true)}
            data-testid="mobile-menu-btn"
          >
            <Menu className="w-5 h-5" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            {/* Notifications */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="relative" data-testid="notifications-btn">
                  <Bell className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {unreadCount}
                    </span>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="flex items-center justify-between">
                  <span>Notifiche</span>
                  {unreadCount > 0 && (
                    <Button variant="ghost" size="sm" onClick={handleMarkAllRead}>
                      Segna tutte lette
                    </Button>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">Nessuna notifica</div>
                ) : (
                  notifications.map((n) => (
                    <DropdownMenuItem key={n.id} className={`flex flex-col items-start gap-1 ${!n.is_read ? 'bg-orange-50' : ''}`}>
                      <span className="font-medium">{n.title}</span>
                      <span className="text-sm text-gray-500">{n.message}</span>
                    </DropdownMenuItem>
                  ))
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                    <span className="text-sm font-medium text-orange-700">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="hidden sm:block text-left">
                    <div className="text-sm font-medium">{user?.name}</div>
                    <Badge className={`text-xs ${roleColors[user?.role]}`}>
                      {roleLabels[user?.role]}
                    </Badge>
                  </div>
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Esci
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">{children}</main>
      </div>

      {/* Chatbot AI (admin only) */}
      <ChatbotDrawer />
    </div>
  );
}
